import { useEffect, useRef, useState } from "react";
import { getTranslatedPath } from "./pageTranslations";

// ============================================================
// Language switcher dropdown rendered in the top-nav-side slot.
//
// Zudoku 0.69.x has no native i18n primitive, so the two
// languages live in parallel `/pages/{en,id}/**` trees and the
// site routes them with a path prefix. This component reads the
// current pathname to detect which language is active, and
// navigates to the equivalent landing page when the user picks
// another from the dropdown.
//
// Sits in the "top-navigation-side" slot (wired in
// zudoku.config.tsx) so it appears on the right edge of the
// top bar, next to the theme toggle — same placement Stripe,
// Twilio, and Vercel docs use for their locale pickers.
//
// We deliberately avoid react-router hooks here: Zudoku
// renders slot content in a sub-tree that lives outside the
// <Router> provider, so `useLocation` / `useNavigate` throw.
// Reading `window.location` + dispatching a `popstate`-aware
// SPA navigation via the History API keeps this slot
// self-contained while still cooperating with Zudoku's
// own client-side router.
// ============================================================

type LangCode = "en" | "id";

type LanguageOption = {
  code: LangCode;
  label: string;
  short: string;
  flag: string;
  landing: string;
};

const LANGUAGES: ReadonlyArray<LanguageOption> = [
  {
    code: "en",
    label: "English",
    short: "EN",
    flag: "🇺🇸",
    landing: "/en/getting-started/introduction",
  },
  {
    code: "id",
    label: "Bahasa Indonesia",
    short: "ID",
    flag: "🇮🇩",
    landing: "/id/memulai/pengenalan",
  },
];

function detectLanguage(pathname: string): LangCode {
  return pathname.startsWith("/id/") || pathname === "/id" ? "id" : "en";
}

function usePathname(): string {
  // Read from window.location and subscribe to popstate/pushstate so the
  // dropdown stays in sync with client-side navigation.
  const [pathname, setPathname] = useState<string>(() =>
    typeof window === "undefined" ? "/" : window.location.pathname,
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const update = () => setPathname(window.location.pathname);

    // Native popstate fires on back/forward. pushState / replaceState don't
    // emit events on their own — monkey-patch them once so any SPA
    // navigation (Zudoku's router included) triggers an update.
    const origPush = window.history.pushState;
    const origReplace = window.history.replaceState;
    type HistoryFn = typeof window.history.pushState;
    const wrap = (fn: HistoryFn): HistoryFn =>
      function (this: History, ...args: Parameters<HistoryFn>) {
        const result = fn.apply(this, args);
        window.dispatchEvent(new Event("zudoku:location"));
        return result;
      };
    window.history.pushState = wrap(origPush);
    window.history.replaceState = wrap(origReplace);

    window.addEventListener("popstate", update);
    window.addEventListener("zudoku:location", update);

    return () => {
      window.removeEventListener("popstate", update);
      window.removeEventListener("zudoku:location", update);
      window.history.pushState = origPush;
      window.history.replaceState = origReplace;
    };
  }, []);

  return pathname;
}

export default function LanguageSwitcher() {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const current =
    LANGUAGES.find((lang) => lang.code === detectLanguage(pathname)) ??
    LANGUAGES[0];

  useEffect(() => {
    if (!open) return;
    function onDocClick(event: MouseEvent) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function onEsc(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  function pick(lang: LanguageOption) {
    setOpen(false);
    if (lang.code === current.code) return;

    // Stay on the equivalent page when one exists (e.g. ai-agent/overview
    // -> ai-agent/ringkasan) instead of always bouncing to the landing
    // page. Falls back to the landing page for EN-only sections
    // (Developer Guides) or any path not in the translation table.
    const target = getTranslatedPath(pathname, lang.code) ?? lang.landing;

    // pushState keeps us in the SPA — our monkey-patched
    // pushState above broadcasts to other listeners (incl. Zudoku
    // itself) so the chrome reacts. Fall back to a hard navigation
    // when the History API is unavailable.
    if (typeof window !== "undefined" && window.history?.pushState) {
      window.history.pushState({}, "", target);
      // Also dispatch popstate so react-router-based listeners
      // (Zudoku internals) treat it as a real navigation.
      window.dispatchEvent(new PopStateEvent("popstate"));
    } else {
      window.location.href = target;
    }
  }

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center gap-1.5 rounded-md border border-border/60 bg-background/60 px-2.5 py-1.5 text-xs font-medium text-foreground/80 hover:text-foreground hover:bg-muted/50 transition"
      >
        <span aria-hidden="true">{current.flag}</span>
        <span>{current.short}</span>
        <svg
          width="10"
          height="10"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
          className={open ? "rotate-180 transition" : "transition"}
        >
          <path
            d="M3 4.5L6 7.5L9 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 min-w-[180px] rounded-md border border-border/60 bg-popover shadow-lg z-50 overflow-hidden"
        >
          {LANGUAGES.map((lang) => {
            const isActive = lang.code === current.code;
            return (
              <button
                key={lang.code}
                role="menuitemradio"
                aria-checked={isActive}
                type="button"
                onClick={() => pick(lang)}
                className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-sm text-left transition ${
                  isActive
                    ? "bg-muted/60 text-foreground"
                    : "text-foreground/80 hover:bg-muted/40 hover:text-foreground"
                }`}
              >
                <span className="flex items-center gap-2">
                  <span aria-hidden="true">{lang.flag}</span>
                  <span>{lang.label}</span>
                </span>
                {isActive && (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M11.6667 3.5L5.25 9.91667L2.33333 7"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
